import { useEffect, useId, useRef, useState, useContext } from 'react';
import { fg } from '@atlassian/jira-feature-gating';
import { useGlobalHydrationActions } from '@atlassian/jira-ssr-controller/src/use-is-page-hydrating.tsx';
import { WaitContext } from '@atlassian/react-loosely-lazy';

type Params = {
	// True if the suspense's fallback or children component has completed rendering
	isSubtreeRendered: boolean;
	name: string;
};

/**
 * This component is rendered at all suspense boundaries (placeholders), to be able to know when
 * hydration has completed globally - when every subtree has rendered.
 */
const useHydrationSubtreeCompleterImpl = ({
	isSubtreeRendered,
	name: unstableName,
}: Params): string => {
	const id = useId();
	const [name] = useState(unstableName);
	const { addPendingSubtree, completePendingSubtree } = useGlobalHydrationActions();

	const waitContext = useContext(WaitContext);

	useEffect(() => {
		if (waitContext?.currentValue() === 0) {
			// no effect if we are inside a LazyWait boundary
			return;
		}
		if (isSubtreeRendered) {
			completePendingSubtree(id);
		} else {
			addPendingSubtree(id, name);
		}
	}, [isSubtreeRendered, id, name, completePendingSubtree, addPendingSubtree, waitContext]);

	// Exists to ensure that we complete a subtree if it unmounts before `isSubtreeRendered` flips to true
	useEffect(() => {
		return () => {
			completePendingSubtree(id);
		};
	}, [id, completePendingSubtree]);

	// prevent dead code removal
	return id;
};

export const useHydrationSubtreeCompleter = (params: Params): void => {
	// Feature gates flip between on/off in storybooks, resulting in React (rightly) complaining that the number of hook
	// calls has changed between renders. This specific hook is a no-op in storybooks anyway, so freeze the condition.
	const enabled = useRef(fg('jfp-magma-fix-hydration-completion')).current;
	if (enabled) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		useHydrationSubtreeCompleterImpl(params);
	}
};
